import {
  Box, Card, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import { ReactNode } from "react";
import { useStudentTotalSummary } from "../hooks/useStudentTotalSummary";

const summaryTiles: { key: "registered" | "active" | "dropped" | "transferred" | "graduated"; label: string; color: string; icon: ReactNode }[] = [
  { key: "registered", label: "Registered", color: "#2563eb", icon: <PersonAddAltOutlinedIcon fontSize="small" /> },
  { key: "active", label: "Active", color: "#16a34a", icon: <CheckCircleOutlineIcon fontSize="small" /> },
  { key: "dropped", label: "Dropped", color: "#dc2626", icon: <PersonRemoveOutlinedIcon fontSize="small" /> },
  { key: "transferred", label: "Transferred", color: "#f59e0b", icon: <SwapHorizOutlinedIcon fontSize="small" /> },
  { key: "graduated", label: "Graduated", color: "#9333ea", icon: <SchoolOutlinedIcon fontSize="small" /> },
];

export const StudentTotalSummaryPage = () => {
  const { summary, loading, error } = useStudentTotalSummary();

  // Backend may send these as null/undefined — never assume presence.
  const totals = {
    registered: summary?.registered ?? 0,
    active: summary?.active ?? 0,
    dropped: summary?.dropped ?? 0,
    transferred: summary?.transferred ?? 0,
    graduated: summary?.graduated ?? 0,
  };
  const gradeWiseData = summary?.gradeWiseData ?? [];
  const academicYearWiseData = summary?.academicYearWiseData ?? [];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Typography variant="h5" fontWeight={600}>
        Student Summary
      </Typography>

      {loading && <Typography color="text.secondary">Loading…</Typography>}
      {error && <Typography color="error">{error}</Typography>}

      {!loading && !error && summary && (
        <>
          <Box>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              Summary
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)", lg: "repeat(6, 1fr)" },
                gap: 1.5,
              }}
            >
              {summaryTiles.map((tile) => (
                <Card key={tile.key} elevation={0} sx={{ border: "1px solid #e5e7eb", borderRadius: 2, p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Typography variant="body2" fontWeight={600}>
                      {tile.label}
                    </Typography>
                    <Box
                      sx={{
                        width: 32, height: 32, borderRadius: "50%",
                        backgroundColor: `${tile.color}1f`, color: tile.color,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {tile.icon}
                    </Box>
                  </Box>
                  <Typography variant="h5" fontWeight={700} sx={{ mt: 1.5, color: tile.color }}>
                    {totals[tile.key].toLocaleString()}
                  </Typography>
                </Card>
              ))}
            </Box>
          </Box>

          <Box>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              Registered, Dropped &amp; Transferred by Grade (Current Academic Year)
            </Typography>
            {gradeWiseData.length === 0 ? (
              <Card elevation={0} sx={{ border: "1px solid #e5e7eb", borderRadius: 2, p: 2.5 }}>
                <Typography variant="body2" color="text.secondary">No grade-wise data available.</Typography>
              </Card>
            ) : (
              <Card elevation={0} sx={{ border: "1px solid #e5e7eb", borderRadius: 2, overflow: "hidden" }}>
                <TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                        <TableCell sx={{ fontWeight: 700 }}>Grade</TableCell>
                        {gradeWiseData.map((g) => (
                          <TableCell key={g.grade} sx={{ fontWeight: 700 }} align="right">{g.grade}</TableCell>
                        ))}
                        <TableCell sx={{ fontWeight: 700 }} align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow hover>
                        <TableCell sx={{ fontWeight: 600 }}>Active</TableCell>
                        {gradeWiseData.map((g) => (
                          <TableCell key={g.grade} align="right">{(g.active ?? 0).toLocaleString()}</TableCell>
                        ))}
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{totals.active.toLocaleString()}</TableCell>
                      </TableRow>
                      <TableRow hover>
                        <TableCell sx={{ fontWeight: 600 }}>Registered</TableCell>
                        {gradeWiseData.map((g) => (
                          <TableCell key={g.grade} align="right">{(g.registered ?? 0).toLocaleString()}</TableCell>
                        ))}
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{totals.registered.toLocaleString()}</TableCell>
                      </TableRow>
                      <TableRow hover>
                        <TableCell sx={{ fontWeight: 600 }}>Dropped</TableCell>
                        {gradeWiseData.map((g) => (
                          <TableCell key={g.grade} align="right">{(g.dropped ?? 0).toLocaleString()}</TableCell>
                        ))}
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{totals.dropped.toLocaleString()}</TableCell>
                      </TableRow>
                      <TableRow hover>
                        <TableCell sx={{ fontWeight: 600 }}>Transferred</TableCell>
                        {gradeWiseData.map((g) => (
                          <TableCell key={g.grade} align="right">{(g.transferred ?? 0).toLocaleString()}</TableCell>
                        ))}
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{totals.transferred.toLocaleString()}</TableCell>
                      </TableRow>
                      <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                        <TableCell sx={{ fontWeight: 700 }}>Total Movement</TableCell>
                        {gradeWiseData.map((g) => {
                          const gradeTotal = (g.registered ?? 0) + (g.dropped ?? 0) + (g.transferred ?? 0);
                          return (
                            <TableCell key={g.grade} align="right" sx={{ fontWeight: 700 }}>
                              {gradeTotal.toLocaleString()}
                            </TableCell>
                          );
                        })}
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {(totals.registered + totals.dropped + totals.transferred).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            )}
          </Box>

          <Box>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              Dropped, Transferred &amp; Graduated by Academic Year
            </Typography>
            {academicYearWiseData.length === 0 ? (
              <Card elevation={0} sx={{ border: "1px solid #e5e7eb", borderRadius: 2, p: 2.5 }}>
                <Typography variant="body2" color="text.secondary">No academic-year-wise data available.</Typography>
              </Card>
            ) : (
              <Card elevation={0} sx={{ border: "1px solid #e5e7eb", borderRadius: 2, overflow: "hidden" }}>
                <TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                        <TableCell sx={{ fontWeight: 700 }}>Academic Year</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">Dropped</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">Transferred</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">Graduated</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {academicYearWiseData.map((row) => {
                        const dropped = row.dropped ?? 0;
                        const transferred = row.transferred ?? 0;
                        const graduated = row.graduated ?? 0;
                        return (
                          <TableRow key={row.academicYear} hover>
                            <TableCell sx={{ fontWeight: 600 }}>{row.academicYear}</TableCell>
                            <TableCell align="right">{dropped.toLocaleString()}</TableCell>
                            <TableCell align="right">{transferred.toLocaleString()}</TableCell>
                            <TableCell align="right">{graduated.toLocaleString()}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                              {(dropped + transferred + graduated).toLocaleString()}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                        <TableCell sx={{ fontWeight: 700 }}>All Years</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{totals.dropped.toLocaleString()}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{totals.transferred.toLocaleString()}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{totals.graduated.toLocaleString()}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {(totals.dropped + totals.transferred + totals.graduated).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};
